// src/services/aiAssistantService.js
import { pb } from "./pocketbase";
import { aiTools, AI_TOOLS_DEFINITIONS } from "./aiTools";
import { normalizeModelName } from "./aiPipelineService";

// Helper para chamadas HTTP com retry
const fetchWithRetry = async (url, options, maxRetries = 1) => {
  let attempt = 0;
  while (attempt <= maxRetries) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errMsg = errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errMsg);
      }
      return response;
    } catch (err) {
      if (attempt === maxRetries) throw err;
      attempt++;
      await new Promise((res) => setTimeout(res, 800));
    }
  }
};

const SYSTEM_PROMPT = `
Você é o Assistente Virtual Oficial da Sempre Joias (fábrica de joias e e-commerce).
Sua missão é ajudar os operadores de bancada, equipe de produção, vendedores do PDV e administradores com respostas rápidas, claras, precisas e 100% determinísticas.

DIRETRIZES FUNDAMENTAIS DE CONFIABILIDADE (RIGOR ABSOLUTO):
1. ZERO ALUCINAÇÃO: Nunca invente dados, nunca presuma números e nunca adivinhe cursos ou peças. Todas as respostas sobre pedidos, prazos e clientes DEVEM vir diretamente dos dados retornados pelas ferramentas.
2. CONSULTAS POR PEDIDO ESPECÍFICO [NÚMERO]:
   - Para saber a situação da fabricação, aro, pedra, gravação, etapa na fábrica ou mural de recados: use 'consultar_pedido_fabrica'.
   - Para saber rastreio dos Correios, envio, destinatário ou Nota Fiscal (NF-e): use 'consultar_pedido_logistica'.
   - Para saber se um pedido vai atrasar, prazo prometido, data prevista calculada, dias restantes na oficina ou folga: use SEMPRE 'consultar_previsao_prazos' com 'orderNumber'.
3. PREVISÃO DE PRAZOS, RISCOS DE ATRASO E CONTROLE DA OFICINA:
   - Para responder perguntas como:
     * "Quais pedidos estão em risco de atraso?"
     * "Temos algum atraso hoje?"
     * "O pedido 71686 vai atrasar?"
     * "Quantos pedidos vão atrasar?"
     * "Quais pedidos estão em revisão?"
     * "Como estão os prazos da oficina?"
     -> Utilize SEMPRE a ferramenta 'consultar_previsao_prazos'.
   - Se a pergunta for sobre um pedido específico (ex: "o pedido 71686 vai atrasar?"), passe 'orderNumber'.
   - Se for um panorama geral da fábrica, passe 'filtro_risco' adequado (ex: 'atrasados', 'vai_atrasar', 'em_revisao' ou 'todos_em_risco').
   - Ao responder sobre prazos:
     * Seja direto e transparente logo no primeiro parágrafo: informe categoricamente se o pedido vai atrasar ou não (ex: "SIM, está em risco iminente de atrasar", "NÃO, está fluindo no prazo", "JÁ ESTÁ ATRASADO").
     * Forneça os números determinísticos: dias decorridos, dias restantes na bancada, data prometida vs data prevista calculada.
     * Cite sempre os pedidos entre colchetes [NÚMERO] (ex: [71686]) para que o sistema gere links clicáveis.
4. PESQUISA DE PEDIDOS NO FLUXO:
   - Para buscar pedidos por nome de cliente: use 'pesquisar_pedidos' com 'termo_cliente'.
   - Para buscar pedidos por dias úteis / idade (ex: "pedidos no 4º dia útil"): use 'pesquisar_pedidos' com 'dias_uteis'.
   - Para buscar pedidos por status logístico (ex: "pedidos prontos para postar"): use 'pesquisar_pedidos' com 'status_logistico'.
   - Para buscar pedidos no arquivo morto / finalizados: use 'pesquisar_pedidos' com 'apenas_arquivados: true'.
5. PRODUTOS E CATÁLOGO:
   - Para consultar detalhes oficiais de um produto pelo seu SKU exato: use 'consultar_produto' (ex: sku.DIR-TRI, sku.BIO-ATL).
6. ORIENTAÇÃO SOBRE ESTOQUE E MEDIDAS:
   - Se o usuário perguntar genericamente sobre estoque, medidas ou aros de cursos (ex: "temos anel de biologia em estoque?", "quais medidas de atlas de direito temos?"):
     -> Responda educadamente: "Para consultar a disponibilidade exata de estoque e medidas sem divergências, por favor informe o SKU específico da joia (ex: sku.DIR-ATL ou sku.BIO-ATL-Z-21) ou consulte diretamente a aba **Estoque**."
     -> Nunca invente quantidades de estoque nem afirme que um produto não existe sem o SKU.
7. FORMATAÇÃO VISUAL:
   - Ao citar pedidos, use sempre colchetes: [71740].
   - Ao citar produtos, use prefixo: sku.CODIGO (ex: sku.DIR-TRI).
   - Use tópicos claros com bullet points e formatação markdown elegante.
8. CONDUTAS, PROCEDIMENTOS OPERACIONAIS E DIRETRIZES DA EMPRESA:
   - Para responder qualquer pergunta sobre condutas com clientes, regras de garantia de 60 dias, política de frete em trocas/consertos, enxoval obrigatório de caixas (capelo, diploma, bombom, flanela), recebimento de devoluções na ISET, inspeção na lupa (regras de microporos), parâmetros de impressão 3D (troca de FEP, limpeza), solda TIG ou manutenção preventiva: utilize SEMPRE a ferramenta 'consultar_manual_treinamento'.
   - Ao responder, cite com clareza o nome da Aula e o código do POP oficial (ex: "Conforme a Aula 2.2 e o POP SAC-01...").
`;

export const aiAssistantService = {
  // --- LISTA TODOS OS PROVEDORES DISPONÍVEIS COM CHAVE DE API ---
  async getAvailableProviders() {
    try {
      const records = await pb.collection("ia_configs").getFullList({
        sort: "nome_exibicao",
      }).catch(() => []);

      return records
        .filter((cfg) => Boolean(cfg.api_key_encrypted || cfg.ativo))
        .map((cfg) => ({
          id: cfg.id,
          provedor: cfg.provedor, // "openai" | "google" | "anthropic"
          nome: cfg.nome_exibicao || (cfg.provedor === "openai" ? "OpenAI (GPT-4o)" : "Google (Gemini)"),
          modelo: cfg.modelo_override || cfg.modelo_padrao || (cfg.provedor === "openai" ? "gpt-4o" : "gemini-2.5-flash"),
          ativo: Boolean(cfg.ativo),
          temChave: Boolean(cfg.api_key_encrypted),
        }));
    } catch (e) {
      return [];
    }
  },

  // --- BUSCA PROVEDOR ATIVO (PADRÃO OPENAI SE TIVER CHAVE) ---
  async getActiveProvider(preferredProviderId = null) {
    try {
      const records = await pb.collection("ia_configs").getFullList({
        sort: "-updated",
      });

      if (!records || records.length === 0) {
        throw new Error(
          "Nenhum provedor de IA cadastrado. Vá em Configurações > Configurações de IA e adicione sua chave da OpenAI ou Gemini."
        );
      }

      let cfg = null;

      // 1. Tenta o ID preferido pelo usuário
      if (preferredProviderId) {
        cfg = records.find((r) => r.id === preferredProviderId && r.api_key_encrypted);
      }

      // 2. Se não houver ID preferido, prioriza OpenAI (GPT) se tiver chave cadastrada
      if (!cfg) {
        cfg = records.find((r) => r.provedor === "openai" && r.api_key_encrypted);
      }

      // 3. Se não houver OpenAI, pega qualquer ativo com chave
      if (!cfg) {
        cfg = records.find((r) => r.ativo && r.api_key_encrypted);
      }

      // 4. Último fallback: qualquer registro com chave
      if (!cfg) {
        cfg = records.find((r) => r.api_key_encrypted) || records[0];
      }

      const key = cfg.api_key_encrypted || "";
      if (!key) {
        throw new Error(
          `O provedor '${cfg.nome_exibicao || cfg.provedor}' não possui chave de API cadastrada em Configurações > Configurações de IA.`
        );
      }

      return {
        id: cfg.id,
        provedor: cfg.provedor,
        nome: cfg.nome_exibicao,
        apiKey: key,
        modelo: cfg.modelo_override || cfg.modelo_padrao || (cfg.provedor === "openai" ? "gpt-4o" : "gemini-2.5-flash"),
      };
    } catch (e) {
      console.error("[aiAssistantService] Erro ao buscar provedor:", e);
      throw e;
    }
  },

  // --- ORQUESTRADOR DE PERGUNTAS E RESPOSTAS ---
  async ask({ query, chatHistory = [], userContext = {}, preferredProviderId = null }) {
    if (!query?.trim()) throw new Error("A pergunta não pode estar vazia.");

    const provider = await this.getActiveProvider(preferredProviderId);

    if (provider.provedor === "openai") {
      return await this.handleOpenAI({ query, chatHistory, userContext, provider });
    } else if (provider.provedor === "google") {
      return await this.handleGemini({ query, chatHistory, userContext, provider });
    } else {
      return await this.handleOpenAI({ query, chatHistory, userContext, provider });
    }
  },

  // --- IMPLEMENTAÇÃO COM OPENAI (TOOL CALLING NATIVO) ---
  async handleOpenAI({ query, chatHistory = [], userContext = {}, provider }) {
    const url = "https://api.openai.com/v1/chat/completions";

    const openAiTools = AI_TOOLS_DEFINITIONS.map((t) => ({
      type: "function",
      function: {
        name: t.name,
        description: t.description,
        parameters: {
          type: "object",
          properties: Object.fromEntries(
            Object.entries(t.parameters.properties).map(([k, v]) => [
              k,
              { ...v, type: (v.type || "string").toLowerCase() },
            ])
          ),
          required: t.parameters.required || [],
        },
      },
    }));

    const messages = [
      { role: "system", content: SYSTEM_PROMPT },
      { role: "user", content: `Usuário Logado: ${userContext.user?.name || "Operador"}\n\n${query}` },
    ];

    // 1ª Rodada
    const firstRes = await fetchWithRetry(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${provider.apiKey}`,
      },
      body: JSON.stringify({
        model: provider.modelo || "gpt-4o",
        messages,
        tools: openAiTools,
        tool_choice: "auto",
        temperature: 0.2,
      }),
    });

    const firstData = await firstRes.json();
    const choice = firstData.choices?.[0]?.message;

    if (choice?.tool_calls && choice.tool_calls.length > 0) {
      const toolCall = choice.tool_calls[0];
      const funcName = toolCall.function.name;
      let funcArgs = {};
      try {
        funcArgs = JSON.parse(toolCall.function.arguments || "{}");
      } catch (_) {}

      const toolResult = await aiTools.executeTool(funcName, funcArgs, userContext);

      messages.push(choice);
      messages.push({
        role: "tool",
        tool_call_id: toolCall.id,
        content: JSON.stringify(toolResult),
      });

      // 2ª Rodada
      const secondRes = await fetchWithRetry(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${provider.apiKey}`,
        },
        body: JSON.stringify({
          model: provider.modelo || "gpt-4o",
          messages,
          temperature: 0.3,
        }),
      });

      const secondData = await secondRes.json();
      return {
        text: secondData.choices?.[0]?.message?.content || "",
        toolUsed: funcName,
        toolResult,
      };
    }

    return {
      text: choice?.content || "Como posso ajudar?",
    };
  },

  // --- IMPLEMENTAÇÃO COM GOOGLE GEMINI (TOOL CALLING NATIVO) ---
  async handleGemini({ query, chatHistory = [], userContext = {}, provider }) {
    const modelName = normalizeModelName("google", provider.modelo || "gemini-2.5-flash");
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${provider.apiKey}`;

    const geminiTools = [
      {
        function_declarations: AI_TOOLS_DEFINITIONS.map((t) => ({
          name: t.name,
          description: t.description,
          parameters: {
            type: "OBJECT",
            properties: Object.fromEntries(
              Object.entries(t.parameters.properties).map(([k, v]) => [
                k,
                { ...v, type: (v.type || "STRING").toUpperCase() },
              ])
            ),
            required: t.parameters.required || [],
          },
        })),
      },
    ];

    const contents = [
      {
        role: "user",
        parts: [{ text: `${SYSTEM_PROMPT}\n\nUsuário Logado: ${userContext.user?.name || "Operador"}\n\nPergunta:\n${query}` }],
      },
    ];

    // 1ª Rodada
    const firstRes = await fetchWithRetry(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents,
        tools: geminiTools,
        generationConfig: { temperature: 0.2 },
      }),
    });

    const firstData = await firstRes.json();
    const candidate = firstData.candidates?.[0]?.content;
    const parts = candidate?.parts || [];

    const functionCallPart = parts.find((p) => p.functionCall);

    if (functionCallPart) {
      const { name, args } = functionCallPart.functionCall;
      const toolResult = await aiTools.executeTool(name, args, userContext);

      contents.push({
        role: "model",
        parts: [{ functionCall: functionCallPart.functionCall }],
      });

      contents.push({
        role: "function",
        parts: [
          {
            functionResponse: {
              name: name,
              response: { result: toolResult },
            },
          },
        ],
      });

      const secondRes = await fetchWithRetry(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents,
          tools: geminiTools,
          generationConfig: { temperature: 0.3 },
        }),
      });

      const secondData = await secondRes.json();
      const finalParts = secondData.candidates?.[0]?.content?.parts || [];
      const finalText = finalParts.map((p) => p.text || "").join("").trim();

      return {
        text: finalText || "Não foi possível formular uma resposta.",
        toolUsed: name,
        toolResult,
      };
    }

    const directText = parts.map((p) => p.text || "").join("").trim();
    return {
      text: directText || "Como posso ajudar?",
    };
  },
};
